import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "../../lib/api";
import { AdminHeader, Modal, ConfirmDelete, Field } from "../../components/admin/AdminUI";
import { ArrowDownUp, Copy, ExternalLink, FileText, Image as ImageIcon, RefreshCw, Search, Upload } from "lucide-react";

const ACCEPTED = ".jpg,.jpeg,.png,.webp,.pdf";

function formatSize(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function isImage(asset) {
  return asset?.media_type === "image";
}

export default function MediaLibraryAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("desc");
  const [openUpload, setOpenUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [replaceFor, setReplaceFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const replaceInput = useRef(null);

  const load = React.useCallback(() => {
    api.get("/admin/media", { params: { q, sort } }).then((r) => setItems(r.data));
  }, [q, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const stats = useMemo(() => {
    const images = items.filter((it) => it.media_type === "image").length;
    const documents = items.filter((it) => it.media_type === "document").length;
    return { images, documents };
  }, [items]);

  const upload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", uploadFile);
    setBusy(true);
    try {
      await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setOpenUpload(false);
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const replace = async (file) => {
    if (!replaceFor || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.put(`/admin/media/${replaceFor.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced");
      setReplaceFor(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (replaceInput.current) replaceInput.current.value = "";
    }
  };

  const copyLink = async (path) => {
    try {
      await navigator.clipboard.writeText(path);
      toast.success("Copied upload path");
    } catch {
      toast.error("Copy failed");
    }
  };

  const del = async (id) => {
    await api.delete(`/admin/media/${id}`);
    load();
    toast.success("Deleted");
  };

  return (
    <div>
      <AdminHeader
        title="Media Library"
        subtitle={`${items.length} files · ${stats.images} images · ${stats.documents} PDFs`}
        actions={
          <button onClick={() => setOpenUpload(true)} className="btn-primary !py-2 !px-4 text-sm" data-testid="media-upload-open">
            <Upload size={16} /> Upload File
          </button>
        }
      />

      <div className="card-elev p-4 mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search media..." className="form-input pl-9" data-testid="media-search" />
        </div>
        <button onClick={() => setSort(sort === "desc" ? "asc" : "desc")} className="btn-ghost justify-center">
          <ArrowDownUp size={16} /> {sort === "desc" ? "Newest first" : "Oldest first"}
        </button>
      </div>

      <input
        ref={replaceInput}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={(e) => replace(e.target.files?.[0])}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((asset) => (
          <div key={asset.id} className="card-elev overflow-hidden">
            <div className="h-44 bg-slate-100 flex items-center justify-center overflow-hidden">
              {isImage(asset) ? (
                <img src={mediaUrl(asset.path)} alt={asset.original_filename} className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-brand-primary">
                  <FileText size={42} className="mx-auto mb-2" />
                  <div className="text-xs font-semibold uppercase tracking-widest">PDF</div>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-primary-light text-brand-primary flex items-center justify-center shrink-0">
                  {isImage(asset) ? <ImageIcon size={18} /> : <FileText size={18} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-brand-text truncate" title={asset.original_filename}>{asset.original_filename}</div>
                  <div className="text-xs text-brand-muted mt-1">{formatSize(asset.size)} · {formatDate(asset.uploaded_at)}</div>
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-brand-bg px-3 py-2 text-xs text-brand-muted font-mono break-all">{asset.path}</div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-brand-border pt-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => copyLink(asset.path)} className="p-2 rounded-lg text-brand-primary hover:bg-brand-primary-light" aria-label="Copy link">
                    <Copy size={16} />
                  </button>
                  <a href={mediaUrl(asset.path)} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-brand-primary hover:bg-brand-primary-light" aria-label="Preview">
                    <ExternalLink size={16} />
                  </a>
                  <button
                    onClick={() => {
                      setReplaceFor(asset);
                      replaceInput.current?.click();
                    }}
                    disabled={busy}
                    className="p-2 rounded-lg text-brand-primary hover:bg-brand-primary-light disabled:opacity-50"
                    aria-label="Replace file"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
                <ConfirmDelete onConfirm={() => del(asset.id)} />
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-brand-muted py-12">No media files found.</div>}
      </div>

      <Modal open={openUpload} onClose={() => setOpenUpload(false)} title="Upload File" size="sm">
        <form onSubmit={upload} className="grid gap-4">
          <Field label="File *">
            <input
              required
              type="file"
              accept={ACCEPTED}
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="form-input"
              data-testid="media-file"
            />
          </Field>
          <div className="text-xs text-brand-muted">Accepted: JPG, JPEG, PNG, WEBP and PDF. Files are stored without compression.</div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpenUpload(false)} className="btn-ghost">Cancel</button>
            <button disabled={busy} className="btn-primary" data-testid="media-upload-submit">
              <Upload size={16} /> {busy ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
